import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { Modal } from "../components/ui/Modal";
import { Button } from "../components/ui/Button";

/**
 * Idle auto-logout.
 *
 *   <IdleLogout idleMs={15 * 60_000} warningMs={60_000} />
 *
 * Tracks user activity (mousemove, keydown, click, touchstart, scroll).
 * After `idleMs` of no activity, shows a "still there?" countdown modal.
 * After `idleMs + warningMs` total, calls auth.signOut("idle").
 *
 * Includes:
 *  • cross-tab synchronisation (a refresh in another tab resets the timer)
 *  • visibilitychange handling (do NOT log out while the tab is hidden;
 *    treat tab-show as activity)
 */
export function IdleLogout({
  idleMs = 15 * 60_000,
  warningMs = 60_000,
}: {
  idleMs?: number;
  warningMs?: number;
}) {
  const { status, signOut } = useAuth();
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(warningMs);
  const lastActivity = useRef<number>(Date.now());
  const channel = useRef<BroadcastChannel | null>(null);

  // Cross-tab activity sync.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    channel.current = new BroadcastChannel("mhpol.activity");
    channel.current.onmessage = (ev) => {
      if (ev.data?.type === "activity") lastActivity.current = ev.data.t;
    };
    return () => { channel.current?.close(); };
  }, []);

  // Activity listeners.
  useEffect(() => {
    if (status !== "authenticated") return;
    const events: (keyof WindowEventMap)[] = ["mousemove", "keydown", "click", "touchstart", "scroll", "wheel"];
    function poke() {
      lastActivity.current = Date.now();
      if (warning) setWarning(false);
      try { channel.current?.postMessage({ type: "activity", t: Date.now() }); } catch { /* ignore */ }
    }
    for (const e of events) window.addEventListener(e, poke, { passive: true });
    return () => { for (const e of events) window.removeEventListener(e, poke); };
  }, [status, warning]);

  // Visibility — treat reveal as activity.
  useEffect(() => {
    const onVis = () => { if (!document.hidden) lastActivity.current = Date.now(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Heartbeat: check every 5s.
  useEffect(() => {
    if (status !== "authenticated") return;
    const id = window.setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      if (idleFor >= idleMs + warningMs) {
        void signOut("idle");
        return;
      }
      if (idleFor >= idleMs) {
        setWarning(true);
        setRemaining(Math.max(0, idleMs + warningMs - idleFor));
      }
    }, 5_000);
    return () => window.clearInterval(id);
  }, [status, idleMs, warningMs, signOut]);

  if (status !== "authenticated") return null;

  return (
    <Modal
      open={warning}
      onClose={() => { lastActivity.current = Date.now(); setWarning(false); }}
      title="Still there? · आपण आहात का?"
      size="sm"
      dismissible={false}
      footer={
        <>
          <Button variant="ghost" onClick={() => signOut("idle-manual")}>Sign out now</Button>
          <Button variant="primary" onClick={() => { lastActivity.current = Date.now(); setWarning(false); }}>
            I&apos;m still here
          </Button>
        </>
      }
    >
      <p className="text-sm text-default">
        For security, you&apos;ll be signed out in <strong>{Math.ceil(remaining / 1000)}</strong> seconds due to inactivity.
      </p>
      <p className="mt-1 text-xs text-muted font-devanagari">
        सुरक्षेसाठी, निष्क्रियतेमुळे काही सेकंदात आपण साइन-आउट व्हाल.
      </p>
    </Modal>
  );
}
